import { computed, inject, Injectable } from '@angular/core';
import { OrderApiResponseStateService } from './order-api-response-state.service';
import { OrderFlowType } from '../../../../../models/order/order.model';
import { DtvIdentityVerificationOption } from '../../../../../models/order/api-models/order-cc-response.model';

@Injectable({ providedIn: 'root' })
export class CreditCheckResponseService {
    private orderApiResponseStateService = inject(OrderApiResponseStateService);

    finalCCResponse = this.orderApiResponseStateService.creditCheckData;
    CCCResponse = this.orderApiResponseStateService.creditCCData;
    CCPlusResponse = this.orderApiResponseStateService.creditCCPlusData;

    internetAttCreditDecision = computed(() => {
        const ccResponse = this.finalCCResponse();
        return ccResponse?.ATTCreditDecision ?? null;
    });

    internetRc1CreditDecision = computed(() => {
        const ccResponse = this.finalCCResponse();
        return ccResponse?.RC1CreditDecision ?? null;
    })

    internetCreditDecision = computed(() => {
        const ccResponse = this.finalCCResponse();
        return ccResponse?.ATTCreditDecision ?? ccResponse?.RC1CreditDecision ?? null;
    });
    videoCreditDecision = computed(() => {
        const ccResponse = this.finalCCResponse();
        return ccResponse?.DTVCreditDecision ?? null;
    });
    wirelessCreditDecision = computed(() => {
        const ccResponse = this.finalCCResponse();
        return ccResponse?.WirelessCreditDecision ?? null;
    });

    hasVideoCreditDecision = computed(() => !!this.videoCreditDecision());
    hasWirelessCreditDecision = computed(() => !!this.wirelessCreditDecision());
    hasInternetCreditDecision = computed(() => !!this.internetCreditDecision());
    hasVoiceCreditDecision = computed(() => !!this.voiceCreditDecision());

    voiceCreditDecision = this.internetCreditDecision;

    getCreditDecisionOnType(type: OrderFlowType) {
        switch (type) {
            case OrderFlowType.Internet:
                return this.internetCreditDecision();
            case OrderFlowType.Video:
                return this.videoCreditDecision();
            case OrderFlowType.Wireless:
                return this.wirelessCreditDecision();
            case OrderFlowType.Voice:
                return this.voiceCreditDecision();
            default:
                return null;
        }
    }

    getErrorResponse(flowType: string) {
        const errors = this.finalCCResponse()?.ResponseHeader?.ProductResponse?.filter(x => String(x?.Code ?? '').trim() !== '0000') ?? [];
        const errorResponse = errors.find(x => x.Type?.toLocaleLowerCase() === flowType.toLocaleLowerCase())
            ?? errors.find(x => x.Type !== 'OVERALL')
            ?? errors[0];
        if (errorResponse) {
            return { code: errorResponse.Code, message: errorResponse.Message || 'Unable to fetch credit check results, please try later.' };
        }
        return null;
    }

    getDTVCreditOptions(): DtvIdentityVerificationOption[] {
        const options = this.CCCResponse().DTVCreditDecision?.CreditOptions?.Option ?? [];
        const normalizedOptions = Array.isArray(options) ? options : [options];
        return normalizedOptions.filter(option => option?.OptionId && option?.OptionText);
    }

    getDTVCreditDecisionMessage = computed(() => {
        const decision = this.videoCreditDecision();
        return decision?.Message ?? null;
    });

    getDTVCCreditDecisionMessage = computed(() => {
        const decision = this.CCCResponse().DTVCreditDecision;
        return decision?.Message ?? null;
    });

    getVideoCCPlusAction = computed(() => {
        const decision = this.videoCreditDecision();
        return decision?.CcPlusAction ?? null;
    });
}
